import { FC, useMemo, useRef, useState, useEffect, useCallback } from 'react';
import moment from 'moment-timezone';
import {
  EventInput,
  DatesSetArg,
  DayHeaderContentArg,
  SlotLabelContentArg,
  EventContentArg,
} from '@fullcalendar/core';
import FullCalendar from '@fullcalendar/react';
import timeGridPlugin from '@fullcalendar/timegrid';
import Text from '@raydiant/raydial/components/Text';
import Button from '@raydiant/raydial/components/Button';
import { ArrowLeft } from '@raydiant/raydial/components/Icons';

type RepeatOption = 'none' | 'daily' | 'annually' | 'weekday' | 'custom';
type CustomFrequency = 'week' | 'day' | 'month' | 'year';
type Weekday = 'SU' | 'MO' | 'TU' | 'WE' | 'TH' | 'FR' | 'SA';

interface PlaylistSchedulePreviewModalV2Props {
  playlistName: string;
  enableScheduling: boolean;
  isRepeating: boolean;
  startDate: string;
  startTime: string;
  endDate: string;
  endTime: string;
  repeatOption: RepeatOption;
  customFreq: CustomFrequency;
  customInterval: number;
  customDays: Weekday[];
  customMonthDay: number;
  customYearDate: string;
}

const weekdayToIso: Record<Weekday, number> = {
  SU: 7,
  MO: 1,
  TU: 2,
  WE: 3,
  TH: 4,
  FR: 5,
  SA: 6,
};

const createEvent = (
  title: string,
  occurrenceStart: moment.Moment,
  durationMs: number,
): EventInput => {
  const end = occurrenceStart.clone().add(durationMs, 'milliseconds');
  return {
    title,
    start: occurrenceStart.toDate(),
    end: end.toDate(),
    backgroundColor: '#4285F4',
    borderColor: '#4285F4',
  };
};

const NOW_COLOR = '#FF6633';
const DOT_PX = 10;
const MAX_PREVIEW_EVENTS = 1200;

const PlaylistSchedulePreviewModalV2: FC<
  PlaylistSchedulePreviewModalV2Props
> = ({
  playlistName,
  enableScheduling,
  isRepeating,
  startDate,
  startTime,
  endDate,
  endTime,
  repeatOption,
  customFreq,
  customInterval,
  customDays,
  customMonthDay,
  customYearDate,
}) => {
  const now = useMemo(() => moment(), []);
  const todayStart = useMemo(() => now.clone().startOf('day'), [now]);
  const scheduleStartDay = useMemo(() => {
    if (!startDate) return null;
    const parsed = moment(startDate, 'YYYY-MM-DD', true);
    return parsed.isValid() ? parsed.startOf('day') : null;
  }, [startDate]);
  const rangeStart = useMemo(
    () =>
      scheduleStartDay && scheduleStartDay.isBefore(todayStart)
        ? scheduleStartDay.clone()
        : todayStart.clone(),
    [scheduleStartDay, todayStart],
  );
  const rangeEnd = useMemo(
    () => now.clone().add(2, 'years').endOf('day'),
    [now],
  );
  const validRangeStart = useMemo(
    () =>
      (scheduleStartDay ? scheduleStartDay.clone() : todayStart.clone())
        .startOf('week')
        .startOf('day'),
    [scheduleStartDay, todayStart],
  );

  const rootRef = useRef<HTMLDivElement>(null);
  const calendarWrapperRef = useRef<HTMLDivElement>(null);
  const overlayLineRef = useRef<HTMLDivElement | null>(null);
  const calendarRef = useRef<FullCalendar | null>(null);
  const [currentTitle, setCurrentTitle] = useState('');
  const [visibleRangeStart, setVisibleRangeStart] =
    useState<moment.Moment | null>(null);
  const [visibleRangeEnd, setVisibleRangeEnd] = useState<moment.Moment | null>(
    null,
  );

  const ensureAxisHeaderIcon = useCallback(() => {
    const wrapper = calendarWrapperRef.current;
    if (!wrapper) return;

    const axisFrame = wrapper.querySelector(
      '.fc-col-header .fc-timegrid-axis .fc-timegrid-axis-frame',
    ) as HTMLElement | null;
    if (!axisFrame) return;
    if (axisFrame.querySelector('.schedule-axis-clock')) return;

    const icon = document.createElement('span');
    icon.className = 'schedule-axis-clock';
    icon.setAttribute('aria-hidden', 'true');
    icon.innerHTML = `
      <svg width="18" height="18" viewBox="0 0 28 28" fill="none" xmlns="http://www.w3.org/2000/svg">
        <circle cx="14" cy="14" r="11.5" stroke="#707070" stroke-width="2.5"/>
        <path d="M14 8.5V14L18 17" stroke="#707070" stroke-width="2.5" stroke-linecap="round"/>
      </svg>
    `;
    axisFrame.appendChild(icon);
  }, []);

  const syncNowLine = useCallback(() => {
    const root = rootRef.current;
    const wrapper = calendarWrapperRef.current;
    if (!root || !wrapper) return;

    const fcLine = wrapper.querySelector(
      '.fc-timegrid-now-indicator-line',
    ) as HTMLElement | null;
    const scroller = wrapper.querySelector(
      '.fc-scroller-liquid-absolute',
    ) as HTMLElement | null;
    if (!fcLine) {
      if (overlayLineRef.current) {
        overlayLineRef.current.style.display = 'none';
      }
      return;
    }

    const rootRect = root.getBoundingClientRect();
    const lineRect = fcLine.getBoundingClientRect();
    const wrapperRect = wrapper.getBoundingClientRect();
    const gridTable = wrapper.querySelector(
      '.fc-timegrid-cols table',
    ) as HTMLElement | null;
    const gridRect = gridTable?.getBoundingClientRect();
    const scrollerRect = scroller?.getBoundingClientRect();

    // Hide the custom line when "now" is outside the currently visible time viewport.
    if (
      scrollerRect &&
      (lineRect.top < scrollerRect.top || lineRect.top > scrollerRect.bottom)
    ) {
      if (overlayLineRef.current) {
        overlayLineRef.current.style.display = 'none';
      }
      return;
    }
    if (!overlayLineRef.current) {
      const line = document.createElement('div');
      line.style.cssText = `
        position: absolute;
        height: 2px;
        background: ${NOW_COLOR};
        z-index: 20;
        pointer-events: none;
      `;

      const leftDot = document.createElement('div');
      leftDot.style.cssText = `
        position: absolute;
        left: 0;
        top: ${-(DOT_PX / 2 - 1)}px;
        width: ${DOT_PX}px;
        height: ${DOT_PX}px;
        border-radius: 50%;
        background: ${NOW_COLOR};
        transform: translateX(-50%);
      `;

      const rightDot = document.createElement('div');
      rightDot.style.cssText = `
        position: absolute;
        right: 0;
        top: ${-(DOT_PX / 2 - 1)}px;
        width: ${DOT_PX}px;
        height: ${DOT_PX}px;
        border-radius: 50%;
        background: ${NOW_COLOR};
        transform: translateX(50%);
      `;

      line.appendChild(leftDot);
      line.appendChild(rightDot);
      root.appendChild(line);
      overlayLineRef.current = line;
    }

    overlayLineRef.current.style.display = '';
    overlayLineRef.current.style.top = `${lineRect.top - rootRect.top}px`;
    overlayLineRef.current.style.left = `${
      (gridRect?.left ?? wrapperRect.left) - rootRect.left
    }px`;
    overlayLineRef.current.style.width = `${
      gridRect?.width ?? wrapperRect.width
    }px`;
  }, []);

  useEffect(() => {
    const wrapper = calendarWrapperRef.current;
    if (!wrapper) return;

    const timeout = setTimeout(syncNowLine, 400);
    const iconTimeout = setTimeout(ensureAxisHeaderIcon, 200);
    const interval = setInterval(syncNowLine, 60_000);

    const scroller = wrapper.querySelector(
      '.fc-scroller-liquid-absolute',
    ) as HTMLElement | null;
    scroller?.addEventListener('scroll', syncNowLine, { passive: true });
    window.addEventListener('resize', syncNowLine);

    return () => {
      clearTimeout(timeout);
      clearTimeout(iconTimeout);
      clearInterval(interval);
      scroller?.removeEventListener('scroll', syncNowLine);
      window.removeEventListener('resize', syncNowLine);
      if (overlayLineRef.current) {
        overlayLineRef.current.remove();
        overlayLineRef.current = null;
      }
    };
  }, [syncNowLine, ensureAxisHeaderIcon]);

  const baseStart = useMemo(() => {
    if (!startDate || !startTime) return null;
    const parsedStart = moment(
      `${startDate} ${startTime}`,
      'YYYY-MM-DD HH:mm',
      true,
    );
    return parsedStart.isValid() ? parsedStart : null;
  }, [startDate, startTime]);

  const baseEnd = useMemo(() => {
    if (!endDate || !endTime) return null;
    const parsedEnd = moment(`${endDate} ${endTime}`, 'YYYY-MM-DD HH:mm', true);
    return parsedEnd.isValid() ? parsedEnd : null;
  }, [endDate, endTime]);

  const events = useMemo(() => {
    if (!enableScheduling || !baseStart || !baseEnd) return [] as EventInput[];
    if (!baseStart.isValid() || !baseEnd.isValid()) return [] as EventInput[];

    const title = playlistName || 'Scheduled content';
    const activeRangeStart = visibleRangeStart ?? rangeStart;
    const activeRangeEnd = visibleRangeEnd ?? rangeEnd;
    const scheduleWindowEnd = baseEnd.isAfter(rangeEnd) ? rangeEnd : baseEnd;
    if (scheduleWindowEnd.isBefore(baseStart)) return [] as EventInput[];

    const baseStartTime = baseStart.clone().second(0).millisecond(0);
    // Preserve the date difference for multi-day ranges (e.g. end is next day)
    // to compute the correct per-occurrence duration instead of collapsing to same day.
    const daysDiff = baseEnd
      .clone()
      .startOf('day')
      .diff(baseStart.clone().startOf('day'), 'days');
    const baseEndTime = baseStart
      .clone()
      .add(daysDiff, 'days')
      .hour(baseEnd.hour())
      .minute(baseEnd.minute())
      .second(0)
      .millisecond(0);

    const isRecurring = isRepeating && repeatOption !== 'none';

    // For recurring schedules, if the end time is midnight (00:00) treat the
    // end date as inclusive by shifting the cutoff to end-of-that-day.
    // This ensures e.g. "Daily until Mar 29 12:00 AM" includes Mar 29 as a
    // play day rather than stopping at the very first moment of Mar 29.
    const isMidnightEnd =
      isRecurring && baseEnd.hour() === 0 && baseEnd.minute() === 0;
    const repeatingEndOfDay = isMidnightEnd
      ? baseEnd.clone().endOf('day')
      : baseEnd;

    let repeatingDurationMs = baseEndTime.diff(baseStartTime);
    if (repeatingDurationMs <= 0 && !isMidnightEnd) {
      // Treat crossing-midnight selections as next-day end time using
      // calendar-day semantics to keep DST transitions accurate.
      repeatingDurationMs = baseEndTime
        .clone()
        .add(1, 'day')
        .diff(baseStartTime);
    }

    const singleDurationMs = scheduleWindowEnd.diff(baseStart);
    if (singleDurationMs <= 0) return [] as EventInput[];

    const occurrenceDurationMs = isRecurring
      ? repeatingDurationMs
      : singleDurationMs;
    // Recurring schedules always preview up to rangeEnd (2 years).
    // endDate/endTime only controls the duration of each occurrence, not when the series ends.
    const rawEffectiveRangeEnd = isRecurring
      ? rangeEnd
      : repeatingEndOfDay.isAfter(rangeEnd)
      ? rangeEnd
      : repeatingEndOfDay;
    const effectiveRangeEnd = rawEffectiveRangeEnd.isAfter(activeRangeEnd)
      ? activeRangeEnd
      : rawEffectiveRangeEnd;
    const items: EventInput[] = [];

    const pushOccurrence = (start: moment.Moment) => {
      if (items.length >= MAX_PREVIEW_EVENTS) return;
      if (start.isAfter(effectiveRangeEnd)) return;
      // For midnight-end recurring schedules, derive the end per occurrence
      // using calendar semantics (next local midnight) so DST transition days
      // land correctly instead of overshooting to 1 AM.
      const durationMs = isMidnightEnd
        ? start.clone().add(1, 'day').startOf('day').diff(start)
        : occurrenceDurationMs;
      if (
        start.clone().add(durationMs, 'milliseconds').isBefore(activeRangeStart)
      ) {
        return;
      }
      items.push(createEvent(title, start, durationMs));
    };

    if (!isRepeating || repeatOption === 'none') {
      pushOccurrence(baseStart);
      return items;
    }

    if (repeatOption === 'weekday' || repeatOption === 'daily') {
      // Device always plays on startDatetime as a standalone first occurrence,
      // regardless of byday — push it before the recurring loop.
      pushOccurrence(baseStartTime);
      const dayAfterBaseStart = baseStart.clone().add(1, 'day');
      let cursor = dayAfterBaseStart.isAfter(activeRangeStart)
        ? dayAfterBaseStart.clone()
        : activeRangeStart
            .clone()
            .hour(baseStart.hour())
            .minute(baseStart.minute())
            .second(baseStart.second())
            .millisecond(baseStart.millisecond());
      if (cursor.isBefore(dayAfterBaseStart)) {
        cursor = dayAfterBaseStart.clone();
      }
      while (
        cursor.isSameOrBefore(effectiveRangeEnd) &&
        items.length < MAX_PREVIEW_EVENTS
      ) {
        const isWeekday = cursor.isoWeekday() >= 1 && cursor.isoWeekday() <= 5;
        if (repeatOption === 'daily' || isWeekday) {
          pushOccurrence(cursor);
        }
        cursor.add(1, 'day');
      }
      return items;
    }

    if (repeatOption === 'annually') {
      const baseMonth = baseStart.month();
      const baseDate = baseStart.date();
      let cursor = baseStart
        .clone()
        .year(Math.max(baseStart.year(), activeRangeStart.year()))
        .month(baseMonth)
        .date(baseDate);
      if (cursor.month() !== baseMonth || cursor.date() !== baseDate) {
        cursor = cursor.clone().month(baseMonth).endOf('month');
      }
      while (cursor.isBefore(baseStart)) {
        cursor.add(1, 'year');
      }
      while (
        cursor.isSameOrBefore(effectiveRangeEnd) &&
        items.length < MAX_PREVIEW_EVENTS
      ) {
        pushOccurrence(cursor);
        const nextYear = cursor.year() + 1;
        let next = cursor
          .clone()
          .year(nextYear)
          .month(baseMonth)
          .date(baseDate);
        // For leap-day style cases, clamp to the last day of the same month.
        if (next.month() !== baseMonth || next.date() !== baseDate) {
          next = cursor.clone().year(nextYear).month(baseMonth).endOf('month');
        }
        cursor = next;
      }
      return items;
    }

    const safeInterval = Math.max(customInterval || 1, 1);
    if (customFreq === 'day') {
      let cursor = baseStart.clone();
      if (cursor.isBefore(activeRangeStart)) {
        const diffDays = activeRangeStart
          .clone()
          .startOf('day')
          .diff(baseStart.clone().startOf('day'), 'days');
        const stepCount = Math.max(Math.floor(diffDays / safeInterval), 0);
        cursor.add(stepCount * safeInterval, 'day');
        while (cursor.isBefore(activeRangeStart)) {
          cursor.add(safeInterval, 'day');
        }
      }
      while (
        cursor.isSameOrBefore(effectiveRangeEnd) &&
        items.length < MAX_PREVIEW_EVENTS
      ) {
        pushOccurrence(cursor);
        cursor.add(safeInterval, 'day');
      }
      return items;
    }

    if (customFreq === 'month') {
      let cursor = baseStart.clone();
      const advanceMonthCursor = (value: moment.Moment, months: number) => {
        const next = value.clone().add(months, 'month');
        const nextInMonth = Math.min(
          Math.max(customMonthDay || 1, 1),
          next.daysInMonth(),
        );
        next.date(nextInMonth);
        return next;
      };
      if (cursor.isBefore(activeRangeStart)) {
        const diffMonths = activeRangeStart
          .clone()
          .startOf('month')
          .diff(baseStart.clone().startOf('month'), 'months');
        const stepCount = Math.max(Math.floor(diffMonths / safeInterval), 0);
        cursor = advanceMonthCursor(cursor, stepCount * safeInterval);
        while (cursor.isBefore(activeRangeStart)) {
          cursor = advanceMonthCursor(cursor, safeInterval);
        }
      }
      while (
        cursor.isSameOrBefore(effectiveRangeEnd) &&
        items.length < MAX_PREVIEW_EVENTS
      ) {
        pushOccurrence(cursor);
        cursor = advanceMonthCursor(cursor, safeInterval);
      }
      return items;
    }

    if (customFreq === 'year') {
      const parsedYearRepeatDate = moment(customYearDate, 'YYYY-MM-DD', true);
      const baseMonth = parsedYearRepeatDate.isValid()
        ? parsedYearRepeatDate.month()
        : baseStart.month();
      const baseDate = parsedYearRepeatDate.isValid()
        ? parsedYearRepeatDate.date()
        : baseStart.date();
      let cursor = baseStart
        .clone()
        .month(baseMonth)
        .date(baseDate)
        .hour(baseStart.hour())
        .minute(baseStart.minute())
        .second(baseStart.second())
        .millisecond(baseStart.millisecond());
      if (cursor.isBefore(baseStart)) {
        cursor.add(safeInterval, 'year');
      }
      if (cursor.isBefore(activeRangeStart)) {
        const diffYears = Math.max(activeRangeStart.year() - cursor.year(), 0);
        const stepCount = Math.floor(diffYears / safeInterval);
        if (stepCount > 0) {
          cursor.add(stepCount * safeInterval, 'year');
        }
        while (cursor.isBefore(activeRangeStart)) {
          cursor.add(safeInterval, 'year');
        }
      }
      while (
        cursor.isSameOrBefore(effectiveRangeEnd) &&
        items.length < MAX_PREVIEW_EVENTS
      ) {
        pushOccurrence(cursor);
        const nextYear = cursor.year() + safeInterval;
        let next = cursor
          .clone()
          .year(nextYear)
          .month(baseMonth)
          .date(baseDate);
        if (next.month() !== baseMonth || next.date() !== baseDate) {
          next = cursor.clone().year(nextYear).month(baseMonth).endOf('month');
        }
        cursor = next;
      }
      return items;
    }

    // custom weekly
    const selectedIsoDays: number[] =
      customDays.length > 0
        ? Array.from(new Set(customDays.map((d) => weekdayToIso[d]))).sort(
            (a, b) => a - b,
          )
        : [baseStart.isoWeekday()];

    const baseWeekStart = baseStart.clone().startOf('isoWeek');
    const diffWeeks = Math.max(
      activeRangeStart.clone().startOf('isoWeek').diff(baseWeekStart, 'weeks'),
      0,
    );
    let weekIndex = Math.floor(diffWeeks / safeInterval) * safeInterval;
    pushOccurrence(baseStartTime);
    while (true) {
      if (items.length >= MAX_PREVIEW_EVENTS) break;
      const weekStart = baseWeekStart.clone().add(weekIndex, 'weeks');
      if (weekStart.isAfter(effectiveRangeEnd)) break;

      if (weekIndex % safeInterval === 0) {
        for (const isoDay of selectedIsoDays) {
          const candidate: moment.Moment = weekStart
            .clone()
            .isoWeekday(isoDay)
            .hour(baseStart.hour())
            .minute(baseStart.minute())
            .second(baseStart.second())
            .millisecond(baseStart.millisecond());

          if (
            candidate.isAfter(baseStart) &&
            candidate.isSameOrAfter(activeRangeStart) &&
            candidate.isSameOrBefore(effectiveRangeEnd)
          ) {
            pushOccurrence(candidate);
          }
        }
      }

      weekIndex += 1;
    }

    return items;
  }, [
    enableScheduling,
    baseStart,
    baseEnd,
    playlistName,
    isRepeating,
    repeatOption,
    customFreq,
    customInterval,
    customDays,
    customMonthDay,
    customYearDate,
    rangeEnd,
    rangeStart,
    visibleRangeStart,
    visibleRangeEnd,
  ]);

  const initialDate = now.toDate();

  const formatRangeTitle = useCallback((start: Date, end: Date) => {
    const startMoment = moment(start);
    const endMoment = moment(end).subtract(1, 'day');
    if (startMoment.month() === endMoment.month()) {
      return `${startMoment.format('MMMM D')} \u2013 ${endMoment.format(
        'D, YYYY',
      )}`;
    }
    return `${startMoment.format('MMMM D')} \u2013 ${endMoment.format(
      'MMMM D, YYYY',
    )}`;
  }, []);

  const renderDayHeader = useCallback((arg: DayHeaderContentArg) => {
    const d = moment(arg.date);
    return (
      <span className="text-xs text-gray-500 font-medium tracking-wide">
        {d.format('ddd').toUpperCase()} {d.format('MM/DD')}
      </span>
    );
  }, []);

  const renderSlotLabel = useCallback(
    (arg: SlotLabelContentArg) => (
      <span className="schedule-time-label">
        {moment(arg.date).format('h:mm A')}
      </span>
    ),
    [],
  );

  const renderEventContent = useCallback((arg: EventContentArg) => {
    const start = moment(arg.event.start);
    const end = moment(arg.event.end);
    const timeStr = `${start.format('h A')} - ${end.format('h A')}`;
    return (
      <div className="px-4 py-2 overflow-hidden">
        <div className="text-white text-[14px] font-normal truncate leading-[1.2]">
          {arg.event.title}
        </div>
        <div className="text-white text-[14px] font-normal truncate leading-[1.2]">
          {timeStr}
        </div>
      </div>
    );
  }, []);

  const handleDatesSet = useCallback(
    (arg: DatesSetArg) => {
      setCurrentTitle(formatRangeTitle(arg.start, arg.end));
      const newStart = moment(arg.start).startOf('day');
      const newEnd = moment(arg.end).subtract(1, 'day').endOf('day');
      setVisibleRangeStart((prev) =>
        prev && prev.isSame(newStart) ? prev : newStart,
      );
      setVisibleRangeEnd((prev) =>
        prev && prev.isSame(newEnd) ? prev : newEnd,
      );
      setTimeout(syncNowLine, 0);
      setTimeout(ensureAxisHeaderIcon, 0);
    },
    [formatRangeTitle, syncNowLine, ensureAxisHeaderIcon],
  );

  const handlePrev = useCallback(() => {
    const api = calendarRef.current?.getApi();
    if (!api) return;
    api.prev();
  }, []);

  const handleNext = useCallback(() => {
    const api = calendarRef.current?.getApi();
    if (!api) return;
    api.next();
  }, []);

  const handleToday = useCallback(() => {
    const api = calendarRef.current?.getApi();
    if (!api) return;
    api.today();
  }, []);

  return (
    <div
      ref={rootRef}
      className="schedule-preview flex-1 min-h-0 flex flex-col pb-4 overflow-visible relative"
    >
      <div className="pt-4 pb-8 shrink-0">
        <Text className="text-gray-700 text-lg">
          This schedule will run on the published device based on the device's
          time zone.
        </Text>
      </div>
      <div
        ref={calendarWrapperRef}
        className="flex-1 min-h-0 rounded-2xl border border-gray-200 overflow-hidden bg-white"
      >
        <div className="border-b border-[#F3F3F3] px-4 py-[15px]">
          <div className="hidden sm:relative sm:flex sm:items-center">
            <Button
              variant="secondary"
              onPress={handleToday}
              className="h-[42px] min-w-[132px] rounded-[16px] border-2 border-[#DCDCDC] bg-white px-6 py-0 text-center text-[16px] font-semibold leading-none text-[#343437]"
            >
              Today
            </Button>
            <div className="absolute left-1/2 -translate-x-1/2 flex items-center gap-4">
              <button
                type="button"
                onClick={handlePrev}
                className="p-1 text-[#343437] hover:text-[#343437]"
                aria-label="Previous week"
              >
                <ArrowLeft size={16} />
              </button>
              <div className="w-[280px] text-center">
                <Text className="relative top-px block truncate text-[18px] leading-none font-semibold text-[#161616]">
                  {currentTitle}
                </Text>
              </div>
              <button
                type="button"
                onClick={handleNext}
                className="p-1 text-[#343437] hover:text-[#343437]"
                aria-label="Next week"
              >
                <ArrowLeft size={16} className="rotate-180" />
              </button>
            </div>
          </div>

          <div className="sm:hidden space-y-3">
            <div className="flex items-center justify-between">
              <button
                type="button"
                onClick={handlePrev}
                className="p-1 text-[#343437]"
                aria-label="Previous week"
              >
                <ArrowLeft size={16} />
              </button>
              <Text className="block max-w-[220px] truncate text-center text-[16px] leading-none font-semibold text-[#161616]">
                {currentTitle}
              </Text>
              <button
                type="button"
                onClick={handleNext}
                className="p-1 text-[#343437]"
                aria-label="Next week"
              >
                <ArrowLeft size={16} className="rotate-180" />
              </button>
            </div>
            <Button
              variant="secondary"
              onPress={handleToday}
              className="h-[42px] w-full rounded-[16px] border-2 border-[#DCDCDC] bg-white px-6 py-0 text-center text-[16px] font-semibold leading-none text-[#343437]"
            >
              Today
            </Button>
          </div>
        </div>
        <div className="h-full overflow-x-auto lg:overflow-x-hidden">
          <div className="calendar-fixed-grid h-full">
            <FullCalendar
              ref={calendarRef}
              plugins={[timeGridPlugin]}
              initialView="timeGridWeek"
              initialDate={initialDate}
              headerToolbar={false}
              datesSet={handleDatesSet}
              titleFormat={{ year: 'numeric', month: 'long', day: 'numeric' }}
              dayHeaderContent={renderDayHeader}
              slotLabelContent={renderSlotLabel}
              eventContent={renderEventContent}
              allDaySlot={false}
              nowIndicator
              editable={false}
              selectable={false}
              weekends
              events={events}
              height="100%"
              scrollTime="08:00:00"
              slotDuration="01:00:00"
              slotLabelInterval="01:00:00"
              slotMinTime="00:00:00"
              slotMaxTime="24:00:00"
              validRange={{
                start: validRangeStart.format('YYYY-MM-DD'),
                end: rangeEnd.format('YYYY-MM-DD'),
              }}
            />
          </div>
        </div>
      </div>

      <style>{`
        .schedule-preview {
          --axis-col-width: 95px;
          --day-col-width: 145px;
          --calendar-total-width: calc(var(--axis-col-width) + (7 * var(--day-col-width)));
        }

        .schedule-preview .fc {
          height: 100% !important;
          overflow: hidden;
          font-family: inherit;
        }

        /* Toolbar */
        .schedule-preview .fc .fc-toolbar {
          padding: 12px 16px;
          margin-bottom: 0;
          border-bottom: 1px solid #F3F3F3;
          display: flex;
          justify-content: space-between;
          align-items: center;
        }
        .schedule-preview .fc .fc-toolbar-chunk {
          display: flex;
          align-items: center;
        }
        .schedule-preview .fc .fc-toolbar-chunk:nth-child(2) {
          flex: 1;
          justify-content: center;
        }
        .schedule-preview .fc .fc-toolbar-chunk:nth-child(2) > div {
          display: flex;
          align-items: center;
          gap: 12px;
        }
        .schedule-preview .fc .fc-toolbar-title {
          font-size: 15px;
          font-weight: 600;
          color: #1f2937;
          margin: 0 8px;
        }

        /* Today button */
        .schedule-preview .fc .fc-today-button {
          background: white !important;
          border: 1px solid #d1d5db !important;
          color: #374151 !important;
          border-radius: 9999px !important;
          padding: 6px 20px !important;
          font-size: 13px !important;
          font-weight: 500 !important;
          text-transform: none !important;
          box-shadow: none !important;
          line-height: 1.4 !important;
        }
        .schedule-preview .fc .fc-today-button:hover {
          background: #f9fafb !important;
        }
        .schedule-preview .fc .fc-today-button:disabled {
          opacity: 0.5 !important;
        }

        /* Nav arrows */
        .schedule-preview .fc .fc-prev-button,
        .schedule-preview .fc .fc-next-button {
          background: none !important;
          border: none !important;
          color: #6b7280 !important;
          padding: 4px !important;
          box-shadow: none !important;
        }
        .schedule-preview .fc .fc-prev-button:hover,
        .schedule-preview .fc .fc-next-button:hover {
          color: #1f2937 !important;
        }

        /* Column headers */
        .schedule-preview .fc .fc-col-header-cell {
          padding: 0;
          border-color: #F3F3F3;
          border-top: none !important;
          background: #f6f6f6;
          position: relative;
        }
        .schedule-preview .fc .fc-scrollgrid-section-header th {
          border-top: none !important;
        }
        .schedule-preview .fc .fc-scrollgrid-section-header > th {
          border-top: none !important;
        }
        .schedule-preview .fc .fc-scrollgrid-section-header,
        .schedule-preview .fc .fc-scrollgrid-section-header > *,
        .schedule-preview .fc .fc-scrollgrid-section-header .fc-scroller-harness,
        .schedule-preview .fc .fc-scrollgrid-section-header .fc-scroller,
        .schedule-preview .fc .fc-col-header {
          border-top: none !important;
          box-shadow: none !important;
        }
        .schedule-preview .fc th.fc-timegrid-axis,
        .schedule-preview .fc .fc-col-header-cell.fc-timegrid-axis {
          padding: 0 !important;
          border-top: none !important;
          border-bottom: none !important;
          border-left: none !important;
          border-right: none !important;
          background: #f6f6f6 !important;
        }
        .schedule-preview .fc .fc-timegrid-axis-frame {
          background: #f6f6f6 !important;
        }
        .schedule-preview .fc .fc-scrollgrid-sync-inner {
          padding: 0 !important;
          margin: 0 !important;
          background: #f6f6f6;
        }
        .schedule-preview .fc .fc-col-header-cell-cushion {
          display: block;
          margin: 0 !important;
          padding: 8px 16px !important;
          box-sizing: border-box;
          color: #707070 !important;
          font-size: 14px !important;
          font-weight: 600 !important;
          background: #f6f6f6;
          text-decoration: none !important;
        }
        .schedule-preview .fc .fc-col-header-cell-cushion span {
          color: #707070 !important;
          font-size: 14px !important;
          font-weight: 600 !important;
          letter-spacing: 0;
        }

        /* Time axis */
        .schedule-preview .fc .fc-timegrid-slot-label {
          padding-right: 8px;
          vertical-align: middle;
        }
        .schedule-preview .fc .fc-timegrid-slot-label-frame {
          height: 100%;
        }
        .schedule-preview .fc .schedule-time-label {
          display: block;
          width: 100%;
          text-align: center;
          font-size: 14px;
          color: #000000;
          font-weight: 400;
          line-height: 1.2;
          padding: 10px;
          box-sizing: border-box;
        }

        /* Grid lines */
        .schedule-preview .fc .fc-scrollgrid {
          border-color: #F3F3F3;
          background: #ffffff;
        }
        .schedule-preview .fc td,
        .schedule-preview .fc th {
          border-color: #F3F3F3;
        }
        .schedule-preview .fc .fc-scrollgrid-section > td {
          border-color: #F3F3F3;
        }

        /* Today column highlight */
        .schedule-preview .fc .fc-day-today {
          background: inherit !important;
        }
        .schedule-preview .fc .fc-timegrid-col.fc-day-today {
          background: transparent !important;
        }
        .schedule-preview .fc .fc-timegrid-col.fc-day-today .fc-timegrid-col-bg {
          background: transparent !important;
        }
        .schedule-preview .fc .fc-col-header-cell.fc-day-today {
          background: #f6f6f6 !important;
        }
        .schedule-preview .fc .fc-col-header-cell.fc-day-today .fc-scrollgrid-sync-inner,
        .schedule-preview .fc .fc-col-header-cell.fc-day-today .fc-col-header-cell-cushion {
          background: #f6f6f6 !important;
        }
        .schedule-preview .fc .fc-col-header-cell.fc-day-today .fc-col-header-cell-cushion,
        .schedule-preview .fc .fc-col-header-cell.fc-day-today .fc-col-header-cell-cushion span {
          color: #111827 !important;
          font-weight: 700 !important;
        }

        /* Events */
        .schedule-preview .fc .fc-timegrid-event {
          border-radius: 8px !important;
          border: none !important;
          box-shadow: none !important;
          overflow: hidden;
        }
        .schedule-preview .fc .fc-timegrid-event .fc-event-main {
          padding: 0 !important;
        }

        /* Hide default FC now indicator */
        .schedule-preview .fc .fc-timegrid-now-indicator-line,
        .schedule-preview .fc .fc-timegrid-now-indicator-arrow {
          opacity: 0 !important;
        }

        /* Table layout */
        .schedule-preview .fc .fc-view-harness {
          overflow: hidden;
        }
        .schedule-preview .fc .fc-scroller-harness {
          overflow: hidden;
        }
        .schedule-preview .fc .fc-scrollgrid-section-header .fc-scroller {
          overflow: hidden !important;
        }
        .schedule-preview .fc .fc-col-header,
        .schedule-preview .fc .fc-timegrid-slots table,
        .schedule-preview .fc .fc-timegrid-cols table {
          width: 100% !important;
          min-width: 100% !important;
        }
        .schedule-preview .fc .fc-timegrid-body {
          width: 100% !important;
          min-width: 100% !important;
        }
        .schedule-preview .fc .fc-scroller {
          overflow-y: auto !important;
          overflow-x: hidden !important;
          -ms-overflow-style: none;
          scrollbar-width: none;
          background: #ffffff;
        }
        .schedule-preview .fc .fc-scroller::-webkit-scrollbar {
          width: 0;
          height: 0;
          display: none;
        }

        /* Visual day-column gaps */
        .schedule-preview .fc .fc-col-header-cell:not(.fc-timegrid-axis) {
          box-shadow: none;
        }
        .schedule-preview .fc .fc-timegrid-col:not(.fc-timegrid-axis) {
          box-shadow: none;
        }
        .schedule-preview .fc .fc-col-header-cell:not(.fc-timegrid-axis),
        .schedule-preview .fc .fc-timegrid-col:not(.fc-timegrid-axis) {
          position: relative;
          overflow: visible;
        }
        .schedule-preview .fc .fc-col-header-cell:not(.fc-timegrid-axis)::before,
        .schedule-preview .fc .fc-timegrid-col:not(.fc-timegrid-axis)::before {
          content: '';
          position: absolute;
          top: -2px;
          left: -3px;
          width: 6px;
          height: calc(100% + 4px);
          background: #ffffff;
          border-left: 1px solid #F3F3F3;
          border-right: 1px solid #F3F3F3;
          pointer-events: none;
          z-index: 8;
        }
        .schedule-preview .fc .fc-col-header-cell:last-child,
        .schedule-preview .fc .fc-timegrid-col:last-child {
          border-right: 1px solid #F3F3F3 !important;
        }
        .schedule-preview .fc .fc-timegrid-event {
          margin: 0;
        }

        /* Clock icon area */
        .schedule-preview .fc .fc-timegrid-axis {
          width: 72px !important;
        }
        .schedule-preview .fc .schedule-axis-clock {
          display: flex;
          align-items: center;
          justify-content: center;
          width: 100%;
          height: 100%;
          line-height: 0;
        }
        .schedule-preview .fc .fc-timegrid-axis-frame {
          display: flex;
          align-items: center;
          justify-content: center;
          width: 100%;
          height: 100%;
          min-height: 100%;
          line-height: 0;
        }
        .schedule-preview .fc .schedule-axis-clock svg {
          display: block;
          width: 18px;
          height: 18px;
        }

        @media (max-width: 1023px) {
          .schedule-preview .calendar-fixed-grid {
            min-width: var(--calendar-total-width);
          }
          .schedule-preview .fc .fc-col-header,
          .schedule-preview .fc .fc-timegrid-body,
          .schedule-preview .fc .fc-timegrid-slots table,
          .schedule-preview .fc .fc-timegrid-cols table {
            width: var(--calendar-total-width) !important;
            min-width: var(--calendar-total-width) !important;
          }
          .schedule-preview .fc .fc-col-header col:first-child,
          .schedule-preview .fc .fc-timegrid-slots col:first-child,
          .schedule-preview .fc .fc-timegrid-cols col:first-child {
            width: var(--axis-col-width) !important;
          }
          .schedule-preview .fc .fc-col-header col:not(:first-child),
          .schedule-preview .fc .fc-timegrid-slots col:not(:first-child),
          .schedule-preview .fc .fc-timegrid-cols col:not(:first-child) {
            width: var(--day-col-width) !important;
          }
        }
      `}</style>
    </div>
  );
};

export default PlaylistSchedulePreviewModalV2;
